import React, { Dispatch, SetStateAction } from 'react'
import styled from '@emotion/styled'
import { useSelector } from 'react-redux'
import { Button } from '@mobi/component-library/Base/Buttons'
import { radius, spacing } from '@mobi/component-library/Foundations'
import { LoadingPlaceholder } from '@mobi/component-library/Misc/LoadingPlaceholder'
import { PullToRefreshContainer } from '@mobi/component-library/Misc/PullToRefreshContainer'
import dayjs from '@mobi/utils/date/extendedDayjs'
import { useRefreshOnBetPlaced } from '@core/Areas/Account/Pages/Activity/Components/Main/hooks'
import { useAccountActivityData } from '@core/Areas/Account/Pages/Activity/Hooks'
import { trackAccountActivityTab } from '@core/Areas/Account/Pages/Activity/helpers/analytics'
import { selectPlayerSessionId } from '@core/Areas/KambiClient/Store/selectors'
import { Coupon } from '@core/Areas/KambiSports/Player/Components/Coupon'
import { KambiBetStatusQuery } from '@core/Areas/KambiSports/Player/helpers/api'
import { isKambiHistoryCoupon } from '@core/Areas/KambiSports/Player/helpers/typeGuard'
import { AutoFetchButton } from '@core/Components/AutoFetchButton'
import { triggerHapticFeedback } from '@core/Utils/hapticFeedback/hapticFeedback'
import { DateHeader } from '../../../Activity/Components/PagesDateHeader'
import { Ticket } from '../../../Activity/Components/Ticket'
import { getIdFromSortedString, isFauxTicketTransaction } from '../../../Activity/helpers'
import type { FilterContent, MyBetsTab } from '../../types'
import { NoBets } from '../NoBets'
import { useGetPlayerKambiBetsHistoryInfiniteQuery } from './Hooks/useGetPlayerKambiBetsHistory'

let timeSinceLastRefresh: number | null = null

export const MyBetsContent: React.FC<{
  activeTab: MyBetsTab
  filterContent: FilterContent
  setSelectedFilter: Dispatch<SetStateAction<FilterContent>>
}> = ({ activeTab, filterContent, setSelectedFilter }) => {
  const [selectedDate, setSelectedDate] = React.useState<string>(() => getCurrentMonth())
  const latestDateRef = React.useRef<string | null>(null)
  useRefreshOnBetPlaced()

  const isRacing = filterContent === 'Racing'
  const isSports = filterContent === 'Sports'
  const isOrderByEventStart = true

  // Reset selected date on tab change & tab analytics
  React.useEffect(() => {
    if (!activeTab) return
    setSelectedDate(getCurrentMonth())
    trackAccountActivityTab({ activeTab })
  }, [activeTab, setSelectedDate])

  const {
    data: racingData,
    isLoading: racingDataIsLoading,
    isError: racingDataIsError,
    isFinalPage: racingDataIsFinalPage,
    isEmptyState: racingDataIsEmptyState,
    refetch: racingDataRefetch,
    fetchNextPage: racingDataFetchNextPage,
    isFetchingNextPage: racingDataIsFetchingNextPage,
  } = useAccountActivityData({
    activeTab,
    isOrderByEventStart,
    selectedDate,
    enabled: !isSports,
  })

  const kambiSessionToken = useSelector(selectPlayerSessionId)
  const {
    kambiHistoryData,
    isLoading: kambiDataIsLoading,
    isFinalPage: kambiDataIsFinalPage,
    refetch: kambiDataRefetch,
    isError: kambiDataIsError,
    isEmptyState: kambiDataIsEmptyState,
    fetchNextPage: kambiDataFetchNextPage,
    isFetchingNextPage: kambiDataIsFetchingNextPage,
  } = useGetPlayerKambiBetsHistoryInfiniteQuery(
    getKambiBetStatus(activeTab),
    !isRacing && !!kambiSessionToken
  )

  React.useEffect(() => {
    if (racingDataIsEmptyState && !kambiDataIsEmptyState) {
      setSelectedFilter('Sports')
    }
  }, [kambiDataIsEmptyState, racingDataIsEmptyState, setSelectedFilter])

  const pagesToRender = isRacing ? racingData?.pages : kambiHistoryData

  const fetchNextPage = () => {
    if (isRacing) return racingDataFetchNextPage()
    if (isSports) return kambiDataFetchNextPage()

    racingDataFetchNextPage()
    kambiDataFetchNextPage()
  }

  const isFetchingNextPage = isRacing ? racingDataIsFetchingNextPage : kambiDataIsFetchingNextPage

  const refetch = () => {
    if (isRacing) return racingDataRefetch()
    if (isSports) return kambiDataRefetch()

    racingDataRefetch()
    kambiDataRefetch()
  }

  const onRefresh = () => {
    if (timeSinceLastRefresh && Date.now() - timeSinceLastRefresh < 2000) return
    setSelectedDate(getCurrentMonth())
    refetch()
    timeSinceLastRefresh = Date.now()
  }

  const isLoadingData = () => {
    if (isSports) return kambiDataIsLoading
    if (isRacing) return racingDataIsLoading || !racingData
    return kambiDataIsLoading || racingDataIsLoading || !racingData || !kambiHistoryData
  }

  if (isLoadingData()) {
    return (
      <ContainerStyled>
        <SkeletonLoader />
      </ContainerStyled>
    )
  }

  if ((isSports && kambiDataIsEmptyState) || (isRacing && racingDataIsEmptyState)) {
    return <NoBets filterContent={filterContent} activeTab={activeTab} />
  }

  return (
    <PullToRefreshContainer
      onRefresh={onRefresh}
      onReachingRefreshThreshold={triggerHapticFeedback}
    >
      <ContainerStyled data-tid-transaction-container>
        {pagesToRender && (
          <>
            {pagesToRender.map(page => {
              if (isKambiHistoryCoupon(page)) {
                const earliestStartDate = page.events.reduce(
                  (earliest, e) => (e.eventStartDate < earliest ? e.eventStartDate : earliest),
                  page.events[0].eventStartDate
                )
                return [
                  <DateHeader
                    key={'date.' + earliestStartDate}
                    isOrderByEvent={true}
                    eventStartDateTime={earliestStartDate}
                    timestamp={earliestStartDate}
                    latestDateRef={latestDateRef}
                    isFirstDate={false}
                  />,
                  <Coupon key={page.couponRef} historyCoupon={page} />,
                ]
              }

              return page.sortedIds.map(ticketId => {
                const item = page.ticketsById[getIdFromSortedString(ticketId)]
                if (isFauxTicketTransaction(item))
                  return (
                    <strong
                      key={ticketId}
                      className='transactions__message-strong'
                      data-testid='transactions-faux-item'
                    >
                      {item.message}
                    </strong>
                  )

                return [
                  <DateHeader
                    key={'date.' + ticketId}
                    isOrderByEvent={true}
                    eventStartDateTime={item.eventStartDateTime}
                    timestamp={item.timestamp}
                    latestDateRef={latestDateRef}
                    isFirstDate={false}
                  />,
                  <Ticket key={ticketId} ticket={item} />,
                ]
              })
            })}
            {!(racingDataIsFinalPage || kambiDataIsFinalPage) && (
              <AutoFetchButton onClick={fetchNextPage} isFetching={isFetchingNextPage} />
            )}
          </>
        )}

        {(racingDataIsError || kambiDataIsError) && (
          <Button
            className='transactions__try-again-button--secondary'
            color='secondary_color'
            data-testid='transactions-try-again'
            onClick={() => refetch()}
          >
            Try again
          </Button>
        )}
      </ContainerStyled>
    </PullToRefreshContainer>
  )
}

// =============
// Local Helpers
// =============

function getCurrentMonth() {
  return dayjs().date(1).format('YYYY-MM-DD')
}

function getKambiBetStatus(activeTab: MyBetsTab): KambiBetStatusQuery {
  switch (activeTab) {
    case 'pending':
      return 'PENDING'
    case 'results':
      return 'SETTLED'
  }
}

// ===============
// Local Component
// ===============

const SkeletonLoader: React.FC = () => (
  <>
    {Array(9)
      .fill(null)
      .map((_, i) => (
        <LoadingPlaceholder key={i} width='100%' height='10rem' borderRadius={radius.lg} />
      ))}
  </>
)

// ======
// Styles
// ======

const ContainerStyled = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: spacing.smx1,
  position: 'relative',
  margin: `1rem ${spacing.md}`,
  minHeight: '100dvh',
  '.transactions__try-again-button--secondary, .transactions__message-strong': {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    fontSize: '1.2rem',
    padding: '2rem',
  },
})
